import type { Language, LocalizedText } from 'src/generated/graphql-manager';

import {
  CheckIcon,
  IconButton,
  InputDescription,
  LanguageIcon,
  Modal,
  Paragraph,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageKey, isSupportedLanguage } from 'src/utils/languages';

import styles from './event-summary-chapters.module.css';

type TitleTranslationsProps = {
  titles: LocalizedText[];
  languages: Language[];
  onSubmit: (titles: LocalizedText[]) => void;
};

export function TitleTranslations({ titles, languages, onSubmit }: TitleTranslationsProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [_titles, setTitles] = useState(titles || []);

  useEffect(() => {
    setTitles(titles || []);
  }, [titles]);

  const revertTitleChanges = () => {
    setIsModalOpen(false);
    setTitles(titles || []);
  };

  return (
    <>
      <IconButton
        aria-label={t('manager.eventSummary.chapters.translateTitles')}
        naked
        onClick={() => setIsModalOpen(true)}
        className={styles.translateButton}
      >
        {titles.every((title) => title.text) ? (
          <>
            <CheckIcon />
            {t('manager.eventSummary.chapters.translatedButton')}
          </>
        ) : (
          <>
            <LanguageIcon />
            {t('manager.eventSummary.chapters.translateButton')}
          </>
        )}
      </IconButton>

      <Modal
        data-testid="titles-modal"
        title={t('manager.eventSummary.chapters.modalTitle')}
        isOpen={isModalOpen}
        onClose={() => revertTitleChanges()}
        contentProps={{ className: styles.titlesModalContent }}
        footer={
          <>
            <SecondaryButton data-testid="titles-modal-cancel-button" onClick={() => revertTitleChanges()}>
              {t('manager.eventSummary.chapters.cancel')}
            </SecondaryButton>

            <PrimaryButton
              disabled={_titles.some((title) => !title.text)}
              data-testid="titles-modal-confirm-button"
              onClick={() => {
                onSubmit(_titles);
                setIsModalOpen(false);
              }}
            >
              {t('manager.eventSummary.chapters.applyTranslations')}
            </PrimaryButton>
          </>
        }
      >
        <Paragraph>{t('manager.eventSummary.chapters.modalDescription')}</Paragraph>

        {_titles.map((title) => {
          let languageLabel = title.language;

          if (isSupportedLanguage(title.language)) {
            languageLabel = t(`common.languages.${getLanguageKey(title.language)}`);
          } else {
            const language = languages.find(({ code }) => code === title.language);
            if (language?.__typename === 'CustomLanguage') languageLabel = language.name;
          }

          return (
            <InputDescription
              key={title.language}
              label={t('manager.eventSummary.chapters.titleLabelForLanguage', { language: languageLabel })}
            >
              <TextInput
                data-testid={`${title.language}-title-input`}
                value={title.text}
                onChange={(event) => {
                  setTitles((state) =>
                    state.map((existingTitle) =>
                      existingTitle.language === title.language
                        ? { ...existingTitle, text: event.target.value }
                        : existingTitle
                    )
                  );
                }}
              />
            </InputDescription>
          );
        })}
      </Modal>
    </>
  );
}
